import type { NivelClase } from '../types'

export interface InfoNivel {
  nivel: NivelClase
  publico: string
  objetivos: string[]
  prioridad: string
  coleccion: string[]
  noAutomatico: string
  plantillaId: string
}

const A_TODOS = ['A01', 'A02', 'A03', 'A04', 'A05', 'A06', 'A07', 'A08', 'A09', 'A10']
const F_TODOS = ['F01', 'F02', 'F03', 'F04', 'F05', 'F06', 'F07', 'F08', 'F09', 'F10']
const V_TODOS = ['V01', 'V02', 'V03', 'V04']

export const NIVELES_INFO: Record<NivelClase, InfoNivel> = {
  'Introducción': {
    nivel: 'Introducción',
    publico: 'Primeras clases o grupos en familiarización.',
    objetivos: [
      'Normas de trabajo',
      'Confianza gradual',
      'Agarre sin fatiga',
      'Control escapular',
      'Core',
      'Coordinación',
      'Nudo y baja altura',
      'Descenso'
    ],
    prioridad: 'Priorizar Piso, Nudo y Baja altura.',
    coleccion: [
      'A01', 'A02', 'A03', 'A04', 'A05', 'A06', 'A07', 'A09',
      'F01', 'F04', 'F06', 'F07', 'F10',
      'T01', 'T02', 'T07',
      'L01', 'L02',
      'E01', 'E02',
      'S01',
      'C01', 'C03',
      ...V_TODOS
    ],
    noAutomatico:
      'No se sugieren automáticamente altura, inversiones aéreas, dinámicos, caídas ni envolturas complejas.',
    plantillaId: 'intro60'
  },
  'Básico': {
    nivel: 'Básico',
    publico: 'Grupos que ya reconocen las normas y buscan consolidar fundamentos.',
    objetivos: [
      'Consolidar llaves',
      'Trepas y descensos',
      'Fuerza específica',
      'Inversiones progresivas',
      'Figuras simples',
      'Secuencias cortas',
      'Trabajo de ambos lados'
    ],
    prioridad: 'Consolidar fundamentos antes de sumar altura o complejidad.',
    coleccion: [
      ...A_TODOS,
      ...F_TODOS,
      'T01', 'T02', 'T03', 'T04', 'T05', 'T07', 'T08',
      'L01', 'L02', 'L03', 'L04', 'L05', 'L06',
      'I01', 'I02', 'I03', 'I04',
      'E01', 'E02', 'E03', 'E04', 'E05', 'E06', 'E07',
      'S01', 'S02', 'S03', 'S05', 'S06',
      'C01', 'C02', 'C03', 'C04',
      ...V_TODOS
    ],
    noAutomatico:
      'No se sugieren automáticamente caídas, dinámicos ni habilidades que dependan de una inversión aérea no consolidada.',
    plantillaId: 'basico60'
  },
  'Intermedio': {
    nivel: 'Intermedio',
    publico: 'Grupos con fundamentos consolidados y trabajo bilateral.',
    objetivos: [
      'Inversión aérea',
      'Llave de cadera y muslo',
      'Entradas y salidas',
      'Resistencia',
      'Transiciones',
      'Lateralidad',
      'Secuencias',
      'Expresión'
    ],
    prioridad: 'Los fundamentos siguen presentes; se suman entradas, salidas y resistencia.',
    coleccion: [
      ...A_TODOS,
      ...F_TODOS,
      'T01', 'T02', 'T03', 'T04', 'T05', 'T06', 'T07', 'T08',
      'L01', 'L02', 'L03', 'L04', 'L05', 'L06', 'L07', 'L08',
      'I01', 'I02', 'I03', 'I04', 'I05', 'I06',
      'E01', 'E02', 'E03', 'E04', 'E05', 'E06', 'E07', 'E08',
      'S01', 'S02', 'S03', 'S04', 'S05', 'S06',
      'C01', 'C02', 'C03', 'C04',
      ...V_TODOS
    ],
    noAutomatico:
      'Los contenidos dinámicos solo se incorporan como contenido personalizado y con confirmación profesional.',
    plantillaId: 'intermedio75'
  },
  'Avanzado': {
    nivel: 'Avanzado',
    publico: 'Grupos con repertorio consolidado que trabajan refinamiento y composición.',
    objetivos: [
      'Refinamiento',
      'Eficiencia',
      'Secuencias complejas',
      'Resistencia',
      'Investigación de transiciones',
      'Composición',
      'Contenido propio'
    ],
    prioridad: 'Se muestran todos los fundamentos porque lo avanzado no elimina las bases.',
    coleccion: [],
    noAutomatico:
      'La aplicación no inventa ni enseña caídas. El contenido avanzado se carga en “Mis contenidos avanzados”.',
    plantillaId: 'avanzado90'
  },
  'Multinivel': {
    nivel: 'Multinivel',
    publico: 'Grupos con distintos recorridos trabajando en simultáneo.',
    objetivos: [
      'Un mismo propósito con tres opciones',
      'Organización de puntos',
      'Rotaciones o estaciones',
      'Observación directa donde hace falta'
    ],
    prioridad:
      'Multinivel no es un nivel superior sino una modalidad de organización de la clase.',
    coleccion: [],
    noAutomatico:
      'La progresión es opcional: nunca es premio ni obligación. El objetivo principal exige Regresión, Base y Progresión.',
    plantillaId: 'multinivel75'
  }
}

export interface PlantillaBloque {
  nombre: string
  minutos: number
  objetivo: string
}

export interface Plantilla {
  id: string
  nombre: string
  duracion: number
  bloques: PlantillaBloque[]
}

export const PLANTILLAS: Record<string, Plantilla> = {
  intro60: {
    id: 'intro60',
    nombre: 'Introducción esencial',
    duracion: 60,
    bloques: [
      { nombre: 'Preparación previa y explicación', minutos: 5, objetivo: 'Explicación y seguridad' },
      { nombre: 'Activación general', minutos: 10, objetivo: 'Activación' },
      { nombre: 'Preparación física específica', minutos: 8, objetivo: 'Preparación específica' },
      { nombre: 'Técnica base', minutos: 15, objetivo: 'Técnica cerca del piso o en nudo' },
      { nombre: 'Objetivo principal', minutos: 12, objetivo: 'Integración de lo conocido' },
      { nombre: 'Vuelta a la calma', minutos: 10, objetivo: 'Vuelta a la calma' }
    ]
  },
  basico60: {
    id: 'basico60',
    nombre: 'Básico progresivo',
    duracion: 60,
    bloques: [
      { nombre: 'Preparación previa y explicación', minutos: 5, objetivo: 'Preparación' },
      { nombre: 'Activación general', minutos: 10, objetivo: 'Activación' },
      { nombre: 'Preparación física específica', minutos: 8, objetivo: 'Fuerza' },
      { nombre: 'Técnica base', minutos: 20, objetivo: 'Técnica' },
      { nombre: 'Integración o secuencia', minutos: 10, objetivo: 'Secuencia' },
      { nombre: 'Vuelta a la calma', minutos: 7, objetivo: 'Vuelta a la calma' }
    ]
  },
  intermedio75: {
    id: 'intermedio75',
    nombre: 'Intermedio técnico',
    duracion: 75,
    bloques: [
      { nombre: 'Preparación previa y explicación', minutos: 5, objetivo: 'Preparación' },
      { nombre: 'Activación general', minutos: 12, objetivo: 'Activación' },
      { nombre: 'Preparación física específica', minutos: 10, objetivo: 'Fuerza' },
      { nombre: 'Técnica base', minutos: 25, objetivo: 'Técnica' },
      { nombre: 'Integración o secuencia', minutos: 15, objetivo: 'Integración' },
      { nombre: 'Vuelta a la calma', minutos: 8, objetivo: 'Vuelta a la calma' }
    ]
  },
  avanzado90: {
    id: 'avanzado90',
    nombre: 'Avanzado y composición',
    duracion: 90,
    bloques: [
      { nombre: 'Preparación previa y explicación', minutos: 10, objetivo: 'Preparación' },
      { nombre: 'Activación general', minutos: 15, objetivo: 'Activación' },
      { nombre: 'Preparación física específica', minutos: 10, objetivo: 'Preparación específica' },
      { nombre: 'Objetivo principal', minutos: 30, objetivo: 'Contenido definido por la profe' },
      { nombre: 'Integración o secuencia', minutos: 15, objetivo: 'Composición' },
      { nombre: 'Vuelta a la calma', minutos: 10, objetivo: 'Vuelta a la calma' }
    ]
  },
  multinivel75: {
    id: 'multinivel75',
    nombre: 'Multinivel por estaciones',
    duracion: 75,
    bloques: [
      { nombre: 'Preparación previa y explicación', minutos: 8, objetivo: 'Organización' },
      { nombre: 'Activación general', minutos: 12, objetivo: 'Activación común' },
      { nombre: 'Preparación física específica', minutos: 10, objetivo: 'Preparación común' },
      {
        nombre: 'Objetivo principal',
        minutos: 30,
        objetivo: 'Estaciones: Regresión, Base y Progresión'
      },
      { nombre: 'Integración o secuencia', minutos: 8, objetivo: 'Integración' },
      { nombre: 'Vuelta a la calma', minutos: 7, objetivo: 'Vuelta a la calma' }
    ]
  }
}
